import { ShieldOff } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/formatters";
import type { PolicyChangeHistoryEntry } from "@/lib/api";
import { AOR_FLAGGED_LOG, AOR_RESTORED_LOG, type AorSource } from "@shared/aor";

export { isAorFlagged } from "@shared/aor";

// CSR-2327 — AOR (Agent of Record change): the customer moved to another
// agency, so the automation can no longer open the policy. The policy stays in
// force — AOR never changes the status the Data Center reports.

export const AOR_INSURED_TOOLTIP =
  "This customer AOR'd away — the automation can no longer access one of their policies.";

export const AOR_FLAG_TOOLTIP =
  "Click here to flag this policy as AOR'd away — the automation can no longer access it";

export const AOR_ALREADY_FLAGGED_TOOLTIP =
  "This policy is already flagged as AOR'd away — use Access restored once LogRock regains access";

// CSR-2333 — an AOR'd policy never goes on a certificate; the COI service
// enforces the same rule on send.
export const AOR_COI_POLICY_LOCKED_REASON =
  "Customer AOR'd away — LogRock can no longer access this policy, so it can't go on a certificate.";

export const AOR_COI_NO_SENDABLE_POLICY_REASON =
  "Customer AOR'd away — LogRock can no longer access this insured's policies, so no certificate can be sent.";

// The activity-log rows an agent's flag / "Access restored" write (CSR-2326),
// titled the same on the Policy Activity tab and the insured timeline.
const AOR_LOG_TITLES: Record<string, string> = {
  [AOR_FLAGGED_LOG.action]: "Policy flagged as AOR'd away",
  [AOR_RESTORED_LOG.action]: "Access restored — AOR'd away flag removed",
};

export function aorLogTitle(log: {
  entityType: string;
  action: string;
}): string | null {
  return log.entityType === AOR_FLAGGED_LOG.entityType
    ? (AOR_LOG_TITLES[log.action] ?? null)
    : null;
}

// The DC change-history fields the "AOR detected" event already tells, left out
// of the raw field-change rows while the experience is on — on the Policy
// Activity tab and the insured timeline alike.
const AOR_CHANGE_FIELDS = new Set(["aorIdentifiedAt", "aorSource", "carrierAgencyCode"]);

export const AOR_DETECTED_TITLE = "AOR detected";
export const AOR_DETECTED_DESCRIPTION =
  "The carrier no longer lets our agent code open this policy, so its data is no longer updated.";

export function withoutAorChanges(
  changeHistory: PolicyChangeHistoryEntry[],
): PolicyChangeHistoryEntry[] {
  return changeHistory.filter((entry) => !AOR_CHANGE_FIELDS.has(entry.field));
}

// A flag the automation raised has no activity-log row of its own (the Data
// Center wrote it during a sync). Each detection is told from the DC change
// history — which survives a later "Access restored" — and, when that history
// is not loaded, from the policy's current flag.
export function aorDetectionTimes(
  changeHistory: PolicyChangeHistoryEntry[],
  current: { identifiedAt: string | null; source: AorSource | null } | null,
): Date[] {
  const detectedAt = changeHistory
    .filter(
      (entry) =>
        entry.field === "aorIdentifiedAt" &&
        entry.newValue &&
        entry.dcSource === "carrier_scrape",
    )
    .map((entry) => new Date(entry.changedAt));
  if (
    detectedAt.length === 0 &&
    current?.identifiedAt &&
    current.source === "carrier_scrape"
  ) {
    detectedAt.push(new Date(current.identifiedAt));
  }
  return detectedAt;
}

export function aorPolicyTooltip(
  identifiedAt: string | null | undefined,
): string {
  return `Customer AOR'd away — identified on ${formatAorDate(identifiedAt)}. The automation can no longer access this policy, so its data is no longer updated.`;
}

export function formatAorDate(identifiedAt: string | null | undefined): string {
  return formatDate(identifiedAt ?? null);
}

export function AorIcon({
  tooltip,
  testId,
  className,
}: {
  tooltip: string;
  testId?: string;
  className?: string;
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            role="img"
            aria-label={tooltip}
            className={cn("inline-flex text-pink-600", className)}
            data-testid={testId}
          >
            <ShieldOff className="w-3.5 h-3.5" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
